import React, { useState, useEffect } from 'react';
import { Table, Tooltip, Button, DatePicker, Select, Checkbox } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { api, useAPI } from '../api';
import dayjs from 'dayjs';


// If you need to use computeDrugCost, ensure it is either imported or defined here.
// For example, if computeDrugCost is defined in another file, you can import it like:
// import { computeDrugCost } from './drugCostUtils'; 
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    CartesianGrid,
    Legend,
    ReferenceLine,
  } from 'recharts';

  const { Option } = Select;

  const monthAbbrevMap = {
    January: 'Jan',
    February: 'Feb',
    March: 'Mar',
    April: 'Apr',
    May: 'May',
    June: 'Jun',
    July: 'Jul',
    August: 'Aug',
    September: 'Sep',
    October: 'Oct',
    November: 'Nov',
    December: 'Dec',
  };
  
  /**
   * PaymentPlanChart
   * @param {Object[]} data - e.g. [ { month: "January", outOfPocket: 400, m3pBill: 200 }, ... ]
   * @param {number} paymentPlanStartMonth - 0-based index (0=Jan, 1=Feb, etc.) for ReferenceLine
   */
  function PaymentPlanChart({ data, paymentPlanStartMonth, showM3P }) {
    // 1. Precompute chart data, removing leading zeros by replacing them with null
    //    so the line does not connect from 0 to the first real cost.
    let firstNonZeroIndex = -1;
    data.forEach((row, idx) => {
      if (firstNonZeroIndex === -1 && row.outOfPocket > 0) {
        firstNonZeroIndex = idx;
      }
    });
  
    const chartData = data.map((row, idx) => {
      const monthAbbrev = monthAbbrevMap[row.month] || row.month;
  
      // If this row is before the first non-zero index, set outOfPocket to null
      // so the line won't be drawn for those months.
      const outOfPocketVal = (firstNonZeroIndex === -1 || idx < firstNonZeroIndex)
        ? null
        : row.outOfPocket || 0;
  
      // Include m3pBill only if M3P is displayed and the row actually has it
      let m3pBillVal;
      if (showM3P && row.m3pBill !== undefined) {
        // Also skip leading zeros for m3pBill if desired (optional):
        m3pBillVal = (firstNonZeroIndex === -1 || idx < firstNonZeroIndex)
          ? null
          : row.m3pBill;
      }
  
      return {
        monthAbbrev,
        outOfPocket: outOfPocketVal,
        ...(m3pBillVal !== undefined ? { m3pBill: m3pBillVal } : {}),
      };
    });
  
    // 2. Compute the X value for Payment Plan reference line only if showM3P is true
    //    and paymentPlanStartMonth is in range
    let refLineX = null;
    if (showM3P && paymentPlanStartMonth >= 0 && paymentPlanStartMonth < chartData.length) {
      refLineX = chartData[paymentPlanStartMonth].monthAbbrev;
    }
  
    // 3. Custom tooltip
    const tooltipFormatter = (value, name) => {
      if (typeof value === 'number') {
        return [`$${value.toFixed(2)}`, name];
      }
      return value;
    };
  
    return (
      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <h3 style={{ marginBottom: 20 }}>Monthly Payment Trend</h3>
        <LineChart
          width={1200}
          height={600}
          data={chartData}
          margin={{ top: 30, right: 30, left: 20, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#ccc" strokeOpacity={0.7} />
          <XAxis
            dataKey="monthAbbrev"
            label={{ value: 'Month', position: 'insideBottom', offset: -15, fontSize: 16 }}
            tick={{ fontSize: 14 }}
          />
          <YAxis
            label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft', offset: 0, fontSize: 16 }}
            tick={{ fontSize: 14 }}
          />
          <RechartsTooltip
            formatter={tooltipFormatter}
            contentStyle={{ fontSize: 14 }}
          />
          <Legend
            verticalAlign="top"
            align="center"
            iconSize={20}
            wrapperStyle={{ fontSize: 16, marginBottom: 20 }}
          />
  
          {/* Always render the Out-of-Pocket line */}
          <Line
            type="linear" // ensures straight lines
            dataKey="outOfPocket"
            name="Out-of-Pocket (w/o M3P)"
            stroke="#2f4b7c"
            strokeWidth={3}
            dot={{ r: 6 }}
            activeDot={{ r: 8 }}
            connectNulls={false} 
          />
  
          {/* Conditionally render the M3P Bill line if showM3P and any row has a non-null m3pBill */}
          {showM3P && chartData.some((row) => row.m3pBill != null) && (
            <Line
              type="linear"
              dataKey="m3pBill"
              name="M3P Bill"
              stroke="#ffa600"
              strokeWidth={3}
              dot={{ r: 6 }}
              activeDot={{ r: 8 }}
              connectNulls={false}
            />
          )}
  
          {/* Payment Plan Start reference line only if showM3P and in range */}
          {refLineX && (
            <ReferenceLine
              x={refLineX}
              stroke="red"
              strokeDasharray="5 5"
              label={{
                value: 'Payment Plan Start',
                position: 'insideTop',
                fill: 'red',
                fontSize: 14,
                fontWeight: 'bold',
              }}
            />
          )}
        </LineChart>
      </div>
    );
  }
  
  
  const formatCost = (
    costType,
    amount,
    retailCost = null,
    deductible = 0,
    coverageLevel1 = null
  ) => {
    // Default object in case we return early
    const defaultResult = {
      costDisplay: 'N/A',
      explanation: '',
      isDeductibleStage: false
    };
  
    // If amount is missing => show 'N/A' unless it's costType=3 with no retail cost
    if (amount === undefined || amount === null || amount === 'N/A') {
      // If costType=3 but we have no valid retailCost => treat as 100% patient responsibility
      if (costType === 3) {
        return {
          costDisplay: '100%',
          explanation: 'No retail cost data found. Patient pays 100% at deductible stage until meet dedutible.',
          isDeductibleStage: true
        };
      }
      return defaultResult;
    }
  
    // ----------------------------------
    // costType=3 => Deductible Logic
    // ----------------------------------
    if (costType === 3) {
      // If no valid retail cost => treat as 100%
      if (typeof retailCost !== 'number') {
        return {
          costDisplay: '100%',
          explanation: 'No retail cost data found. Patient pays 100% at deductible stage until meet dedutible.',
          isDeductibleStage: true
        };
      }
  
      // This signals we are in the deductible stage
      let isDeductibleStage = true;
  
      // 1) If retail cost <= deductible => pay 100% of retail cost
      if (retailCost <= deductible) {
        return {
          costDisplay: `$${retailCost.toFixed(2)}`,
          explanation: `Retail cost ($${retailCost.toFixed(
            2
          )}) <= deductible ($${deductible.toFixed(2)}). Patient pays full cost.`,
          isDeductibleStage
        };
      }
  
      // 2) If retail cost > deductible => difference = retail - deductible
      const costDifference = retailCost - deductible;
      if (!coverageLevel1) {
        // Fallback if coverage-level-1 missing
        return {
          costDisplay: `$${retailCost.toFixed(2)}`,
          explanation: `No coverage-level-1 info; default to full retail cost.`,
          isDeductibleStage
        };
      }
  
      let finalCost = 0;
      let explanation = '';
      const cov1Type = parseInt(coverageLevel1.cost_type_pref, 10);
      const cov1Amount = parseFloat(coverageLevel1.cost_amt_pref);
  
      // coverage-level-1 is a percentage
      if (cov1Type === 2) {
        finalCost = deductible + costDifference * cov1Amount;
        explanation = `Retail cost = $${retailCost.toFixed(
          2
        )} > deductible = $${deductible.toFixed(
          2
        )}. Pay $${deductible.toFixed(
          2
        )} + ${(cov1Amount * 100).toFixed(
          0
        )}% of $${costDifference.toFixed(2)} = $${finalCost.toFixed(2)}.`;
      }
      // coverage-level-1 is a fixed copay
      else if (cov1Type === 1) {
        finalCost = deductible + cov1Amount;
        explanation = `Retail cost = $${retailCost.toFixed(
          2
        )} > deductible = $${deductible.toFixed(
          2
        )}. Pay $${deductible.toFixed(2)} + $${cov1Amount.toFixed(
          2
        )} = $${finalCost.toFixed(2)}.`;
      } else {
        // Fallback if not recognized
        finalCost = retailCost;
        explanation = `Could not determine coverage-level-1 type; default full cost = $${retailCost.toFixed(2)}.`;
      }
  
      return {
        costDisplay: `$${finalCost.toFixed(2)}`,
        explanation,
        isDeductibleStage
      };
    }
  
    // ----------------------------------
    // Other cost types
    // ----------------------------------
    if (costType === 0) {
      return {
        costDisplay: `$${parseFloat(amount).toFixed(2)}`,
        explanation: '',
        isDeductibleStage: false
      };
    }
  
    if (costType === 2) {
      const percentage = parseFloat(amount);
      let display = `${(percentage * 100).toFixed(0)}%`;
      let explanation = '';
      if (retailCost) {
        const costVal = (retailCost * percentage).toFixed(2);
        display += ` ($${costVal})`;
        explanation = `Pay ${(percentage * 100).toFixed(0)}% of $${retailCost.toFixed(2)}.`;
      }
      return {
        costDisplay: display,
        explanation,
        isDeductibleStage: false
      };
    }
  
    if (costType === 1) {
      return {
        costDisplay: `$${parseFloat(amount).toFixed(2)}`,
        explanation: '',
        isDeductibleStage: false
      };
    }
  
    // Fallback if unknown
    return {
      costDisplay: amount,
      explanation: '',
      isDeductibleStage: false
    };
  };

  const processAndDisplayInsulinCostNew = (insulinCostData, daysSupplyFilter, pharmacyType, startDate, frequency ) => {
    if (!Array.isArray(insulinCostData)) {
      return {
        retailCost: null,
        costBreakdown: [],
        message: 'No insulin cost data available'
      };
    }
  
    // Determine days supply as numeric: 1 for 30days, 2 for 90days (default to 1)
    const daysSupplyValue = frequency === '30days' ? 1 : (frequency === '90days' ? 2 : 1);
  
    // Coverage levels for labeling
    const coverageLevels = {
      0: '0. Deductible',
      1: '1. Initial Coverage',
      3: '2. Catastrophic'
    };
  
    // Prepare coverage info as an array of objects with numeric cost values.
    // We default to null for level 0 and 1 and to 0 for catastrophic (level 3)
    let coverageInfo = [
      { level: '0', preferredCost: null, nonPreferredCost: null },
      { level: '1', preferredCost: null, nonPreferredCost: null },
      { level: '3', preferredCost: 0,    nonPreferredCost: 0 } // Catastrophic is usually $0
    ];
  
    // Populate coverageInfo from insulinCostData for matching daysSupply
    insulinCostData.forEach(cost => {
      if (cost.days_supply === daysSupplyValue) {
        let rawPreferredCost, rawNonPreferredCost;
        if (pharmacyType === 'retail') {
          rawPreferredCost = cost.copay_amt_pref_insln ?? cost.copay_amt_nonpref_insln;
          rawNonPreferredCost = cost.copay_amt_nonpref_insln;
        } else { // mail pharmacy
          rawPreferredCost = cost.copay_amt_mail_pref_insln ?? cost.copay_amt_mail_nonpref_insln;
          rawNonPreferredCost = cost.copay_amt_mail_nonpref_insln;
        }
        // Convert to a number if possible; otherwise, remain null.
        const numericPreferred = (rawPreferredCost !== null && rawPreferredCost !== undefined)
          ? parseFloat(rawPreferredCost)
          : null;
        const numericNonPreferred = (rawNonPreferredCost !== null && rawNonPreferredCost !== undefined)
          ? parseFloat(rawNonPreferredCost)
          : null;
  
        // If we found a valid numeric cost, set both level 0 and level 1 costs.
        if (numericPreferred !== null && !isNaN(numericPreferred)) {
          coverageInfo[0].preferredCost = numericPreferred;
          coverageInfo[0].nonPreferredCost = numericNonPreferred;
          coverageInfo[1].preferredCost = numericPreferred;
          coverageInfo[1].nonPreferredCost = numericNonPreferred;
        }
      }
    });
  
    // If initial coverage (level 1) is missing, then set catastrophic (level 3) to null as well.
    if (coverageInfo[1].preferredCost === null) {
      coverageInfo[2].preferredCost = null;
      coverageInfo[2].nonPreferredCost = null;
    }
  
    // Use level 1 cost as the retail cost
    let numericRetailCost = coverageInfo[1].preferredCost;
  
    // Build the costBreakdown array with the proper fields.
    // We now include a numeric "coverage_level" and a "costNumber" field.
    const insulinBreakdown = coverageInfo.map(info => {
      const numericCost = info.preferredCost;
      return {
        coverage_level: parseInt(info.level, 10),  // 0, 1, or 3
        stage: coverageLevels[info.level],
        costNumber: numericCost, // numeric value or null
        costDisplay: (numericCost !== null && !isNaN(numericCost))
          ? `$${numericCost.toFixed(2)}`
          : 'N/A',
        percentageDisplay: null,  // No percentage value for insulin
        explanation: `Insulin cost at ${coverageLevels[info.level]}`,
        isDeductibleStage: parseInt(info.level, 10) === 0
      };
    });
  
    return {
      retailCost: numericRetailCost, // a number (e.g., 35)
      costBreakdown: insulinBreakdown
    };
  };

  export const getCoverageLevelWord = (level) => {
    const coverageLevels = {
      0: '0. Deductible',
      1: '1. Initial Coverage',
      // 2: '2. Coverage Gap',
      3: '2. Catastrophic',
    };
    return coverageLevels[level] || 'Unknown';
  };
// New Helper: computeDrugCost
// ----------------------
// This function mimics the logic in patientOutOfPocketCostRender but returns a computed cost value.
// It returns an object with { drugName, cost } where cost is a number (if available).

function normalizeDrugName(name) {
    if (!name) return "";
    // Convert to lowercase.
    let normalized = name.toLowerCase();
    // Remove digits.
    normalized = normalized.replace(/\d+/g, "");
    // Remove dosage units (mg, ml, etc.)
    normalized = normalized.replace(/\b(mg|ml|mcg|g)\b/g, "");
    // Remove common stopwords (tablet, oral, capsule, injection, etc.)
    normalized = normalized.replace(/\b(tablet|oral|capsule|injection|suspension|solution|strip|kit)\b/g, "");
    // Remove punctuation and extra whitespace.
    // eslint-disable-next-line
    normalized = normalized.replace(/[\/\-\[\]\(\),]/g, " ");
    normalized = normalized.replace(/\s+/g, " ").trim();
    return normalized;
  }
  
function computeDrugCost(record, selectedDaysSupply, selectedPharmacyType, deductible, drugCosts, startDate, frequency ) {
      // Check if the drug is not covered.
      console.log('record:',record)
      // Use the provided frequency (or default to the record’s frequency).
    // Likewise, override the prescribedDate with startDate if provided.
    const effectiveFrequency = frequency || record.frequency;
    const effectivePrescribedDate = startDate || record.prescribedDate;

      if (record.covered === false) {
        return {
          drugName: record.nonproprietaryname || record.proprietaryname || 'Unknown',
          frequency: effectiveFrequency,
          prescribedDate: effectivePrescribedDate,
          retailCost: null,
          costBreakdown: [],
          notCovered: true
        };
      }
  
    // Return early if out_of_pocket_cost data is missing.
    if (!Array.isArray(record.out_of_pocket_cost)) {
        return {
          drugName: record.nonproprietaryname || record.proprietaryname || 'Unknown',
          frequency: effectiveFrequency,
          prescribedDate: effectivePrescribedDate,
          retailCost: null,
          costBreakdown: []
        };
    }
    selectedPharmacyType = selectedPharmacyType || 'retail';
  
    // Use effectiveFrequency (override record.frequency if provided) to determine daysSupplyValue.
  // For instance, if effectiveFrequency is "30days" or "30" we assign 1 (otherwise, 2).
  const daysSupplyValue =
    effectiveFrequency === '30days' || effectiveFrequency === '30'
        ? 1
        : 2;
    console.log(
    "computeDrugCost:",
    record.nonproprietaryname,
    "frequency:",
    effectiveFrequency,
    "-> daysSupplyValue:",
    daysSupplyValue
    );
  
    const isInsulin = record.out_of_pocket_cost.some(cost => cost.coverage_level === undefined);
  
    const mailOrderNotOffered = record.out_of_pocket_cost.some(cost =>
      cost.days_supply === daysSupplyValue &&
      cost.cost_amt_mail_pref === 'not offered' &&
      cost.cost_amt_mail_nonpref === 'not offered'
    );
    if (mailOrderNotOffered && selectedPharmacyType === 'mail') {
      return {
        drugName: record.nonproprietaryname || record.proprietaryname || 'Unknown',
        retailCost: null,
        costBreakdown: []
      };
    }
  
    if (deductible > 0 && !record.out_of_pocket_cost.some(cost => cost.coverage_level === 0 && cost.days_supply === daysSupplyValue)) {
      const deductibleStageEntry = {
        coverage_level: 0,
        days_supply: daysSupplyValue,
        cost_type_pref: 3,
        cost_type_nonpref: 3,
        cost_type_mail_pref: 3,
        cost_type_mail_nonpref: 3,
        cost_amt_pref: '1.00',
        cost_amt_nonpref: '1.00',
        cost_amt_mail_pref: '1.00',
        cost_amt_mail_nonpref: '1.00'
      };
      record.out_of_pocket_cost.unshift(deductibleStageEntry);
    }
  
    // For insulin, you might apply a different logic. For now, we return an empty breakdown.
    if (isInsulin) {
      const insulinCostResult = processAndDisplayInsulinCostNew(record.out_of_pocket_cost, selectedDaysSupply, selectedPharmacyType, startDate, frequency);
      console.log ('insulinCostResult:',insulinCostResult)
      return {
        drugName: record.nonproprietaryname || record.proprietaryname || 'Unknown',
        frequency: effectiveFrequency,
        prescribedDate: effectivePrescribedDate,
        retailCost: insulinCostResult.retailCost,
        costBreakdown: insulinCostResult.costBreakdown,
        tier_level_value: record.tier_level_value
      };
    }
  
    // Build a retail cost map from drugCosts.
    const retailCostMap = {};
    if (Array.isArray(drugCosts)) {
      drugCosts.forEach(cost => {
        // Normalize the cost's drug_name
        const normalizedCostKey = normalizeDrugName(cost.drug_name);
        if (!retailCostMap[normalizedCostKey]) {
          retailCostMap[normalizedCostKey] = { retail: null, mail: null };
        }
        if (cost.pharmacy_name.toLowerCase().includes("mail")) {
          retailCostMap[normalizedCostKey].mail = parseFloat(cost.retail_cost || 0);
        } else {
          retailCostMap[normalizedCostKey].retail = parseFloat(cost.retail_cost || 0);
        }
      });
    }
  
    let nonproprietaryKey = '';
    if (record.nonproprietaryname) {
      // Remove "(generic)" if present (case-insensitive), then lowercase and trim.
      nonproprietaryKey = record.nonproprietaryname.replace(/\(generic\)/i, "").toLowerCase().trim();
    } else if (record.proprietaryname) {
      nonproprietaryKey = record.proprietaryname.split(" ")[0].toLowerCase();
    }
  
    const normalizedRecordKey = normalizeDrugName(nonproprietaryKey);
  
    let matchingCosts;
    if (retailCostMap.hasOwnProperty(normalizedRecordKey)) {
      matchingCosts = [normalizedRecordKey, retailCostMap[normalizedRecordKey]];
    } else {
      // Fallback: find a key that includes our normalizedRecordKey.
      matchingCosts = Object.entries(retailCostMap).find(([drugNameKey]) =>
        drugNameKey.includes(normalizedRecordKey)
      );
    }
    
    const retailCosts = matchingCosts ? matchingCosts[1] : { retail: 0, mail: 0 };
  
    let selectedPharmacyCost = 0;
    // Check against effectiveFrequency instead of record.frequency
    if (selectedPharmacyType === "mail") {
      selectedPharmacyCost = (effectiveFrequency === '30days' || effectiveFrequency === '30')
        ? (retailCosts.mail !== null && retailCosts.mail !== 0 ? retailCosts.mail / 3 : null)
        : (retailCosts.mail !== null && retailCosts.mail !== 0 ? retailCosts.mail : null);
    } else {
      selectedPharmacyCost = (effectiveFrequency === '30days' || effectiveFrequency === '30')
        ? (retailCosts.retail !== null && retailCosts.retail !== 0 ? retailCosts.retail : null)
        : (retailCosts.retail !== null && retailCosts.retail !== 0 ? retailCosts.retail * 3 : null);
    }
    
  
    // Save the computed retail cost to return later.
    const computedRetailCost = selectedPharmacyCost;
  
    const coverageLevel1Entry = record.out_of_pocket_cost.find(
      (c) => c.coverage_level === 1 && c.days_supply === daysSupplyValue
    );
  
    // Filter and sort cost rows.
    const sortedAndFilteredCosts = record.out_of_pocket_cost
      .filter(cost => cost.days_supply === daysSupplyValue && cost.coverage_level !== 2)
      .sort((a, b) => a.coverage_level - b.coverage_level);
  
    if (sortedAndFilteredCosts.length === 0) {
      return {
        drugName: record.nonproprietaryname || record.proprietaryname || 'Unknown',
        frequency: effectiveFrequency,
        prescribedDate: effectivePrescribedDate,
        retailCost: computedRetailCost,
        costBreakdown: []
      };
    }
  
    // Map each cost row to a breakdown item.
    const costBreakdown = sortedAndFilteredCosts.map(costRow => {
      let pharmacyKey = costRow.coverage_level === 0 
        ? 'cost_amt_pref' 
        : (selectedPharmacyType === 'retail' ? 'cost_amt_pref' : 'cost_amt_mail_pref');
    
      const costResult = formatCost(
        costRow.cost_type_pref,
        costRow[pharmacyKey],
        typeof selectedPharmacyCost === 'number' ? selectedPharmacyCost : null,
        deductible,
        coverageLevel1Entry
      );
    
      let costNumber = null;
      let percentageDisplay = null;
      if (costResult.costDisplay) {
        // If the display string starts with a dollar sign, extract the numeric value.
        if (costResult.costDisplay.startsWith('$')) {
          costNumber = parseFloat(costResult.costDisplay.replace('$',''));
        } else if (costResult.costDisplay.includes('%')) {
          // Example format: "21% ($196.01)"
          const regex = /^([\d.]+%)\s*\(\$([\d.]+)\)/;
          const match = costResult.costDisplay.match(regex);
          if (match) {
            percentageDisplay = match[1]; // e.g., "21%"
            costNumber = parseFloat(match[2]); // e.g., 196.01
          } else {
            // Fallback: try to extract a $ value even if the regex doesn't match perfectly.
            const dollarRegex = /\$([\d.]+)/;
            const dollarMatch = costResult.costDisplay.match(dollarRegex);
            if (dollarMatch) {
              costNumber = parseFloat(dollarMatch[1]);
            }
          }
        }
      }
      return {
        coverage_level: costRow.coverage_level,
        stage: getCoverageLevelWord(costRow.coverage_level),
        costNumber,
        costDisplay: costResult.costDisplay,
        percentageDisplay,  // new field for the percentage part (if any)
        explanation: costResult.explanation,
        isDeductibleStage: costResult.isDeductibleStage
      };
    });
  
    return {
      drugName: record.nonproprietaryname || record.proprietaryname || 'Unknown',
      frequency: effectiveFrequency,
      prescribedDate: effectivePrescribedDate,
      retailCost: computedRetailCost,
      costBreakdown,
      tier_level_value: record.tier_level_value
    };
}
  
  
function MonthlyBreakdown({
  results,
  selectedDaysSupply,
  selectedPharmacyType,
  selectedPlanDetails,
  drugCosts,
  // externalPaymentPlanStartMonth (if passed in) is ignored in favor of our local control
}) {
  // State hooks for breakdown and simulation data.
  // const [allBreakdownData, setAllBreakdownData] = useState([]);
  const [displayedBreakdownData, setDisplayedBreakdownData] = useState([]);
  const [yearlySummary, setYearlySummary] = useState([]);
  const [simulationWarnings, setSimulationWarnings] = useState([]);

  // New state for starting date and frequency
  // eslint-disable-next-line
  const [startDate, setStartDate] = useState(new Date()); // default starting date is today
  // eslint-disable-next-line
  const [frequency, setFrequency] = useState('30days');   // default frequency is 30 days

  // New state for Payment Plan – a checkbox and selection.
  const [showM3P, setShowM3P] = useState(false);
  const [paymentPlanStartMonth, setPaymentPlanStartMonth] = useState(startDate.getMonth());

    // State for cap message.
  const [capMessage, setCapMessage] = useState('');

  console.log("Payment Plan Start Month (0-based):", paymentPlanStartMonth);
  console.log("Default Start Date:", startDate);
  console.log("Default Frequency:", frequency);
  console.log("Results received:", results);


  const handleFrequencyChange = (rowIndex, newFreq) => {
    setDisplayedBreakdownData((prev) => {
      const updated = [...prev];
      const row = { ...updated[rowIndex] };
      row.frequency = newFreq;
      // Recompute cost using the original record so we have all raw data
      const newComputed = computeDrugCost(
        row.originalRecord,
        selectedDaysSupply,
        selectedPharmacyType,
        selectedPlanDetails?.deductible || 0,
        drugCosts,
        row.prescribedDate,
        newFreq
      );
      // Update computed fields:
      row.retailCost = newComputed.retailCost;
      row.deductibleCost = newComputed.costBreakdown.find(item => item.coverage_level === 0)?.costNumber || null;
      row.initialCoverageCost = newComputed.costBreakdown.find(item => item.coverage_level === 1)?.costNumber || null;
      row.percentageDisplay = newComputed.costBreakdown.find(item => item.coverage_level === 1)?.percentageDisplay || null;
      updated[rowIndex] = row;
      return updated;
    });
  };

  const handleDateChange = (rowIndex, newDate) => {
    setDisplayedBreakdownData((prev) => {
      const updated = [...prev];
      const row = { ...updated[rowIndex] };
      row.prescribedDate = newDate;
      // Recompute cost using the original record
      const newComputed = computeDrugCost(
        row.originalRecord,
        selectedDaysSupply,
        selectedPharmacyType,
        selectedPlanDetails?.deductible || 0,
        drugCosts,
        newDate,
        row.frequency
      );
      row.retailCost = newComputed.retailCost;
      row.deductibleCost = newComputed.costBreakdown.find(item => item.coverage_level === 0)?.costNumber || null;
      row.initialCoverageCost = newComputed.costBreakdown.find(item => item.coverage_level === 1)?.costNumber || null;
      row.percentageDisplay = newComputed.costBreakdown.find(item => item.coverage_level === 1)?.percentageDisplay || null;
      updated[rowIndex] = row;
      return updated;
    });
  };

  // Function to remove a drug (if needed)
  const handleRemoveDrug = (drugName) => {
    setDisplayedBreakdownData((prev) => prev.filter((d) => d.drugName !== drugName));
  };

  // 1. Compute breakdownData per drug (individual drug details)
  useEffect(() => {
    const newData = [];
    results.forEach(record => {
      const computed = computeDrugCost(
        record,
        selectedDaysSupply,
        selectedPharmacyType,
        selectedPlanDetails?.deductible || 0,
        drugCosts,
        startDate,       // from your MonthlyBreakdown state
        frequency        // from your MonthlyBreakdown state
      );
      console.log(
        "Drug:",
        computed.drugName,
        "percentageDisplay in coverage stage?",
        computed.costBreakdown.some(item => item.percentageDisplay)
      );
      const tierLevel = computed.notCovered ? "Not Covered" : record.tier_level_value;
      
      if (computed.notCovered) {
        newData.push({
          originalRecord: record,
          key: computed.drugName,
          drugName: computed.drugName,
          frequency: computed.frequency,
          prescribedDate: computed.prescribedDate,
          tierLevel,
          retailCost: null,
          deductibleCost: null,
          initialCoverageCost: null,
          percentageDisplay: null,
          costShareLabel: "Not Covered",
          notCovered: true
        });
      } else {
        const deductibleStage = computed.costBreakdown.find(item => item.coverage_level === 0);
        const initialCoverageStage = computed.costBreakdown.find(item => item.coverage_level === 1);
        const dedCost = deductibleStage ? deductibleStage.costNumber : null;
        const initCost = initialCoverageStage ? initialCoverageStage.costNumber : null;
        const perc = initialCoverageStage ? initialCoverageStage.percentageDisplay : null;

        let costShareLabel;
        if (dedCost == null && initCost == null) {
          costShareLabel = 'N/A';
        } else {
          costShareLabel = perc ? `Cost share: ${perc}` : 'Co-pay';
        }
        
        newData.push({
          originalRecord: record,
          key: computed.drugName,
          drugName: computed.drugName,
          frequency: computed.frequency,
          prescribedDate: computed.prescribedDate,
          tierLevel,
          retailCost: computed.retailCost,
          deductibleCost: dedCost,
          initialCoverageCost: initCost,
          percentageDisplay: perc,
          costShareLabel,
          notCovered: false
        });
      }
    });
    // setAllBreakdownData(newData);
    setDisplayedBreakdownData(newData);  // Initially show all
  }, [results, selectedDaysSupply, selectedPharmacyType, selectedPlanDetails, drugCosts, startDate, frequency]);

  // 2. Run simulation for monthly breakdown in 2025.
  useEffect(() => {
    const planDeductible = selectedPlanDetails?.deductible || 0;
    let remainingDeductible = planDeductible;
    let trueOutOfPocket = 0;
    const catastrophicLimit = 2000;
    
    const monthlyTotals = Array.from({ length: 12 }, () => 0);
    const monthlyDrugs = Array.from({ length: 12 }, () => []);
    const monthlyExplanations = Array.from({ length: 12 }, () => []);
    const monthlyTrueOOP = Array.from({ length: 12 }, () => 0);
    const monthlyOOPArray = Array.from({ length: 12 }, () => 0);
    
    const startOfYear = new Date(2025, 0, 1);
    const endOfYear = new Date(2025, 11, 31);

    // Filter drugs for simulation.
    const drugsForSimulation = [];
    const warnings = [];

    displayedBreakdownData.filter(drug => !drug.notCovered).forEach(drug => {
      if (drug.deductibleCost == null && drug.initialCoverageCost == null) {
        warnings.push(`Price data is missing for ${drug.drugName}.`);
      } else if (drug.retailCost == null) {
        warnings.push(`Retail cost is missing for ${drug.drugName}, skipping simulation.`);
      } else {
        drugsForSimulation.push(drug);
      }
    });
    setSimulationWarnings(warnings);
    warnings.forEach(warning => console.warn(warning));

    // Gather and process refill events.
    const events = [];
    drugsForSimulation.forEach(drug => {
      const frequencyDays = parseInt(drug.frequency, 10);
      if (isNaN(frequencyDays) || frequencyDays <= 0) return;
      let eventDate = new Date(drug.prescribedDate);
      while (eventDate < startOfYear) {
        eventDate.setDate(eventDate.getDate() + frequencyDays);
      }
      while (eventDate <= endOfYear) {
        events.push({
          date: new Date(eventDate),
          drugName: drug.drugName,
          retailCost: drug.retailCost,
          deductibleCost: drug.deductibleCost,
          initialCoverageCost: drug.initialCoverageCost,
          percentageDisplay: drug.percentageDisplay,
          frequency: drug.frequency
        });
        eventDate.setDate(eventDate.getDate() + frequencyDays);
      }
    });
    events.sort((a, b) => a.date - b.date);
    
    const eventsByMonth = Array.from({ length: 12 }, () => []);
    events.forEach(event => {
      const m = event.date.getMonth();
      eventsByMonth[m].push(event);
    });


    for (let m = 0; m < 12; m++) {
      let monthOOP = 0; 
      // eslint-disable-next-line no-loop-func
      eventsByMonth[m].forEach(event => {
        let eventCost = 0;
        let explanationText = "";
        const retail = event.retailCost;
        const initCost = event.initialCoverageCost;
        const percDisplay = event.percentageDisplay;
        const dedCost = event.deductibleCost;
        
        if (remainingDeductible > 0) {
          if (retail <= remainingDeductible) {
            if (dedCost != null) {
              eventCost = dedCost;
              explanationText = `Deductible stage: $${dedCost.toFixed(2)} applied`;
              remainingDeductible -= dedCost;
              trueOutOfPocket += dedCost;
              monthOOP += dedCost;
            } else {
              eventCost = retail;
              explanationText = `Deductible stage: $${retail.toFixed(2)} applied.`;
              remainingDeductible -= retail;
              trueOutOfPocket += retail;
              monthOOP += retail;
            }
          } else {
            const diff = retail - remainingDeductible;
            if (percDisplay) {
              const perc = parseFloat(percDisplay.replace('%','')) / 100;
              eventCost = remainingDeductible + (perc * diff);
              explanationText = `Deductible stage: remaining deductible $${remainingDeductible.toFixed(2)} + ${percDisplay} of diff $${diff.toFixed(2)} = $${eventCost.toFixed(2)}.`;
              trueOutOfPocket += remainingDeductible + (0.25 * diff);
              monthOOP += remainingDeductible + (0.25 * diff);
              remainingDeductible = 0;
            } else {
              const candidate = remainingDeductible + initCost;
              const candidateOOP = remainingDeductible + (0.25 * diff);
              eventCost = candidate;
              explanationText = `Deductible stage (co-payment coverage): remaining deductible $${remainingDeductible.toFixed(2)} + initial coverage cost $${(initCost || 0).toFixed(2)} = $${candidate.toFixed(2)}; OOP candidate $${candidateOOP.toFixed(2)}; using $${eventCost.toFixed(2)} for True OOP.`;
              trueOutOfPocket += Math.max(candidateOOP, candidate);
              monthOOP += Math.max(candidateOOP, candidate);
              remainingDeductible = 0;
            }
          }
        } else if (trueOutOfPocket < catastrophicLimit) {
          const potentialIncrease = 0.25 * retail;
          const remainingTrOOP = catastrophicLimit - trueOutOfPocket;
          eventCost = initCost;
          explanationText = `Initial coverage stage: base event cost = $${initCost.toFixed(2)}; `;
          if (potentialIncrease > remainingTrOOP) {
            if (percDisplay) {
              eventCost = Math.min(remainingTrOOP, initCost);
              explanationText += `capped by remaining TrOOP ($${remainingTrOOP.toFixed(2)}).`;
            } else {
              explanationText += `event cost remains at $${initCost.toFixed(2)}.`;
            }
            trueOutOfPocket += remainingTrOOP;
            monthOOP += remainingTrOOP;
          } else {
            // Instead of capping, choose the larger value between the base event cost (initCost) and potentialIncrease
            const finalAdded = Math.max(eventCost, potentialIncrease);
            if (finalAdded === potentialIncrease) {
              explanationText += `true OOP increases by 25%×retail $${potentialIncrease.toFixed(2)}.`;
            } else {
              explanationText += `true OOP increases by $${finalAdded.toFixed(2)} (exceeds 0.25×retail).`;
            }
            trueOutOfPocket += finalAdded;
            monthOOP += finalAdded;
          }          
        } else {
          eventCost = 0;
          explanationText = `Catastrophic stage reached; event cost = $0.`;
        }
        
        monthlyTotals[m] += eventCost;
        if (!monthlyDrugs[m].includes(event.drugName)) {
          monthlyDrugs[m].push(event.drugName);
        }
        monthlyExplanations[m].push(`On ${event.date.toLocaleDateString()} for ${event.drugName}: ${explanationText}`);
      });
      monthlyTrueOOP[m] = trueOutOfPocket;
      monthlyOOPArray[m] = monthOOP;
    }
    
    const monthNames = ["January", "February", "March", "April", "May", "June",
                        "July", "August", "September", "October", "November", "December"];
    let summaryRows = monthNames.map((name, idx) => ({
      key: name,
      month: name,
      cost: monthlyTotals[idx],
      drugs: monthlyDrugs[idx].join(', '),
      trueOOP: monthlyTrueOOP[idx],
      monthlyOOP: monthlyOOPArray[idx],
      explanation: monthlyExplanations[idx].join(' | ')
    }));
    
    const totalMonths = 12;
    const annualOOPThreshold = 2000;
    let remainingBalanceDue = 0; 
    let cumulativeTrueOOPPaid = 0;

    summaryRows = summaryRows.map((row, index) => {
      if (index < paymentPlanStartMonth) {
        cumulativeTrueOOPPaid += row.cost;
        return { ...row, m3pMonthlyCap: null, m3pBill: null, balanceInM3P: null, m3pExplanation: "Payment plan not started."};
      } else {
        const monthsRemaining = totalMonths - index;
        if (index === paymentPlanStartMonth) {
          const amountToReachThreshold = annualOOPThreshold - cumulativeTrueOOPPaid;
          const monthlyCap = amountToReachThreshold > 0 ? amountToReachThreshold / monthsRemaining : 0;
          const m3pBill = Math.min(monthlyCap, row.cost + (remainingBalanceDue / monthsRemaining));
          const balanceInM3P = remainingBalanceDue + (row.cost - m3pBill);
          cumulativeTrueOOPPaid += row.cost;
          remainingBalanceDue = balanceInM3P;
          const m3pExplanation = `Start Payment Plan: previous OOP = $${(cumulativeTrueOOPPaid - row.cost).toFixed(2)}; needed = $${amountToReachThreshold.toFixed(2)}; monthly cap = $${monthlyCap.toFixed(2)}; Bill = $${m3pBill.toFixed(2)}; balance = $${balanceInM3P.toFixed(2)}.`;
          return { ...row, m3pMonthlyCap: monthlyCap, m3pBill, balanceInM3P, m3pExplanation };
        } else {
          const monthsRemaining = totalMonths - index;
          const adjustedRemainingBalance = remainingBalanceDue + row.cost;
          const monthlyCap = adjustedRemainingBalance / monthsRemaining;
          const m3pBill = Math.min(monthlyCap, row.cost + (remainingBalanceDue / monthsRemaining));
          const balanceInM3P = remainingBalanceDue + (row.cost - m3pBill);
          cumulativeTrueOOPPaid += row.cost;
          remainingBalanceDue = balanceInM3P;
          const m3pExplanation = `Month ${row.month}: adjusted balance = $${adjustedRemainingBalance.toFixed(2)}; monthly cap = $${monthlyCap.toFixed(2)}; Bill = $${m3pBill.toFixed(2)}; balance = $${balanceInM3P.toFixed(2)}.`;
          return { 
            ...row, 
            m3pMonthlyCap: monthlyCap, 
            m3pBill, 
            balanceInM3P,
            m3pExplanation 
          };
        }
      }
    });
    
    const totalM3PBill = summaryRows.reduce((sum, row) => sum + (row.m3pBill || 0), 0);
    
    const totalAnnual = monthlyTotals.reduce((sum, v) => sum + v, 0);

    // Now compute the cap message.
    let capMsg = '';
    const startIndex = startDate.getMonth();

    const reachedIndex = summaryRows.findIndex(row => row.trueOOP >= annualOOPThreshold);
    if (reachedIndex !== -1) {
      const reachedMonth = summaryRows[reachedIndex].month;
      const monthsTaken = reachedIndex - startIndex + 1;
      capMsg = `Patient will reach the $2000 cap in ${reachedMonth}, 2025, which takes ${monthsTaken} month(s), starting from ${monthNames[startIndex]}, with a total out-of-pocket of $${totalAnnual.toFixed(2)}.`;
    } else {
      capMsg = `Patient will NOT reach the $2000 cap starting from ${monthNames[startIndex]}; by the end of December, the total out-of-pocket is $${totalAnnual.toFixed(2)}.`;
    }
    setCapMessage(capMsg);

    summaryRows.push({
      key: "Annual",
      month: "Annual Total",
      cost: totalAnnual,
      drugs: "",
      trueOOP: trueOutOfPocket,
      explanation: "",
      m3pMonthlyCap: null,
      m3pBill: totalM3PBill,
      balanceInM3P: remainingBalanceDue,
      m3pExplanation: null,
    });
    
    setYearlySummary(summaryRows);
  }, [displayedBreakdownData, selectedPlanDetails, paymentPlanStartMonth, startDate]);

  // Prepare chart data; if M3P is off, omit M3P fields.
  const chartData = yearlySummary
    .filter(row => row.month !== "Annual Total")
    .map(row => ({
      month: row.month,
      outOfPocket: row.cost,
      ...(showM3P ? { m3pBill: row.m3pBill } : {})
    }));

  // Define table columns for the Monthly Breakdown view.
  const stageColumns = [
    { title: 'Drug Name', dataIndex: 'drugName', key: 'drugName' },
    {
    title: 'Frequency',
    dataIndex: 'frequency',
    key: 'frequency',
    // The third argument to `render` is `rowIndex` if you're not using pagination or sorting.
    // Otherwise, you may need a different way of identifying the row (e.g., record.key).
    render: (val, record, rowIndex) => (
      <Select
        style={{ width: 100 }}
        value={val}
        onChange={(newFreq) => handleFrequencyChange(rowIndex, newFreq)}
      >
        <Option value="30days">30 Days</Option>
        <Option value="90days">90 Days</Option>
      </Select>
    ),
    },
    {
        title: 'Start Date',
        dataIndex: 'prescribedDate',
        key: 'prescribedDate',
        render: (dateVal, record, rowIndex) => (
          <DatePicker
            // defaultValue={dayjs('2019-09-03')}
            value={dateVal ? dayjs(dateVal) : null}
            onChange={(date) =>
              handleDateChange(rowIndex, date ? date.toDate() : null)
            }
          />
        ),
    },
    {
      title: 'Tier Level',
      dataIndex: 'tierLevel',
      key: 'tierLevel'
    },
    {
      title: 'Deductible Cost',
      dataIndex: 'deductibleCost',
      key: 'deductibleCost',
      render: cost => (typeof cost === 'number' ? `$${cost.toFixed(2)}` : 'N/A')
    },
    {
      title: 'Initial Coverage Cost',
      dataIndex: 'initialCoverageCost',
      key: 'initialCoverageCost',
      render: cost => (typeof cost === 'number' ? `$${cost.toFixed(2)}` : 'N/A')
    },
    {
      title: 'Cost Share Type',
      dataIndex: 'costShareLabel',
      key: 'costShareLabel',
      render: val => val || 'Co-pay'
    },
    {
      title: 'Retail Cost',
      dataIndex: 'retailCost',
      key: 'retailCost',
      render: cost => (typeof cost === 'number' ? `$${cost.toFixed(2)}` : 'N/A')
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          style={{ 
            backgroundColor: '#f5f5f5', 
            color: '#000', 
            borderColor: '#f5f5f5' 
          }}
          onClick={() => handleRemoveDrug(record.drugName)}
        >
          Remove
        </Button>
      )
    }
  ];

    // Define two versions of yearly simulation columns.
  const yearlyColumnsFull = [
    { title: 'Month', dataIndex: 'month', key: 'month' },
    { title: 'Drugs Refilled', dataIndex: 'drugs', key: 'drugs' },
    { 
      title: (
        <Tooltip placement="bottom" title="The Monthly Total Amount patients pay at the pharmacy if not enrolled in M3P.">
          Out-of-Pocket (w/o M3P) <QuestionCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'cost', 
      key: 'cost', 
      onCell: () => ({
        style: { backgroundColor: '#f5f5f5' }
      }),
      render: (cost, record) => (
        <Tooltip title={record.explanation}>
          {cost != null ? `$${cost.toFixed(2)}` : 'N/A'}
        </Tooltip>
      ) 
    },
    { 
      title: (
        <Tooltip placement="bottom" title="The amount from the current month that counts toward overall True OOP total.">
          Monthly True OOP <QuestionCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'monthlyOOP', 
      key: 'monthlyOOP', 
      render: val => (val != null ? `$${val.toFixed(2)}` : 'N/A')
    },
    { 
      title: (
        <Tooltip placement="bottom" title="The running total of True Out-of-pocket spending since the start of the year.">
          True OOP Sum <QuestionCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'trueOOP', 
      key: 'trueOOP', 
      render: val => (val != null ? `$${val.toFixed(2)}` : 'N/A')
    },
    { 
      title: (
        <Tooltip placement="bottom" title="The Maximum Amount patients would be charged in this month if in M3P.">
          M3P Monthly Cap <QuestionCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'm3pMonthlyCap', 
      key: 'm3pMonthlyCap',  
      render: cap => (cap != null ? `$${cap.toFixed(2)}` : 'N/A')
    },
    { 
      title: (
        <Tooltip placement="bottom" title="The actual M3P bill patients will receive.">
          M3P Bill (w/ M3P) <QuestionCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'm3pBill', 
      key: 'm3pBill', 
      onCell: () => ({
        style: { backgroundColor: '#f5f5f5' }
      }),
      render: (bill, record) => (
        <Tooltip title={record.m3pExplanation || "No M3P explanation available"}>
          {bill != null ? `$${bill.toFixed(2)}` : 'N/A'}
        </Tooltip>
      ) 
    },
    { 
      title: (
        <Tooltip placement="bottom" title="The remaining balance in the M3P plan after the current month's bill.">
          Balance in M3P <QuestionCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'balanceInM3P', 
      key: 'balanceInM3P',
      render: bal => (bal != null ? `$${bal.toFixed(2)}` : 'N/A')
    }
  ];

  const yearlyColumnsSimple = [
    { title: 'Month', dataIndex: 'month', key: 'month' },
    { title: 'Drugs Refilled', dataIndex: 'drugs', key: 'drugs' },
    { 
      title: (
        <Tooltip placement="bottom" title="The Monthly Total Amount patients pay at the pharmacy.">
          Out-of-Pocket (w/o M3P) <QuestionCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'cost', 
      key: 'cost', 
      onCell: () => ({ style: { backgroundColor: '#f5f5f5' } }),
      render: (cost, record) => (
        <Tooltip title={record.explanation}>
          {cost != null ? `$${cost.toFixed(2)}` : 'N/A'}
        </Tooltip>
      ),
    },
    { 
      title: (
        <Tooltip placement="bottom" title="The amount from the current month that counts toward overall True OOP total.">
          Monthly True OOP <QuestionCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'monthlyOOP', 
      key: 'monthlyOOP', 
      render: val => (val != null ? `$${val.toFixed(2)}` : 'N/A'),
    },
    { 
      title: (
        <Tooltip placement="bottom" title="The running total of True Out-of-pocket spending since the start of the year.">
          True OOP Sum <QuestionCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'trueOOP', 
      key: 'trueOOP', 
      render: val => (val != null ? `$${val.toFixed(2)}` : 'N/A'),
    },
  ];

  // Choose which yearly columns to render based on the showM3P checkbox.
  const yearlyColumnsToRender = showM3P ? yearlyColumnsFull : yearlyColumnsSimple;
  const sendEmailNotification = useAPI(api.sendEmailNotification);

  return (
    <div>
      <h3>Drug Cost Breakdown</h3>
      <Table dataSource={displayedBreakdownData} columns={stageColumns} pagination={false} />

      {simulationWarnings.length > 0 && (
        <div style={{ marginBottom: '1rem', color: 'red' }}>
          <strong>Warning:</strong>
          <ul>
            {simulationWarnings.map((warning, idx) => <li key={idx}>{warning}</li>)}
          </ul>
        </div>
      )}

      {/* Payment Plan Feature Controls */}
      <div style={{ marginBottom: 16, border: '1px solid #ddd', padding: '1rem' }}>
        <Checkbox
            checked={showM3P}
            onChange={(e) => {
              setShowM3P(e.target.checked);
              if (e.target.checked) {
                sendEmailNotification({
                  subject: 'Payment Plan View',
                  body: 'Payment Plan view in the monthly breakdown section was selected.',
                });
              }
            }}
            style={{ marginBottom: 8 }}
          >
          Display cost under M3P (Medicare Prescription Payment Plan )
        </Checkbox>
        {showM3P && (
          <div style={{ marginTop: 8 }}>
            <span style={{ marginRight: 8 }}>Payment Plan Start Month:</span>
            <Select
              value={paymentPlanStartMonth}
              onChange={(val) => setPaymentPlanStartMonth(val)}
              style={{ width: 150 }}
            >
              <Option value={0}>January</Option>
              <Option value={1}>February</Option>
              <Option value={2}>March</Option>
              <Option value={3}>April</Option>
              <Option value={4}>May</Option>
              <Option value={5}>June</Option>
              <Option value={6}>July</Option>
              <Option value={7}>August</Option>
              <Option value={8}>September</Option>
              <Option value={9}>October</Option>
              <Option value={10}>November</Option>
              <Option value={11}>December</Option>
            </Select>
          </div>
        )}
      </div>

      {/* Simulation Section */}
      <h3 style={{ marginTop: '2rem' }}>Monthly Breakdown (Jan - Dec 2025)</h3>
      <p>*All costs are estimates for reference.</p>
      {/* Display cap message in its own box */}
      <div style={{ backgroundColor: '#f5f5f5', padding: '1rem', marginBottom: '1rem', fontWeight: 'bold' }}>
        {capMessage}
      </div>
      <Table dataSource={yearlySummary} columns={yearlyColumnsToRender} pagination={false} />
      {chartData.length > 0 && (
        <PaymentPlanChart
          data={chartData}
          paymentPlanStartMonth={paymentPlanStartMonth}
          showM3P={showM3P}
        />
      )}
    </div>
  );
}

export default MonthlyBreakdown;